import type { DeliveryZone, LatLng } from '@agos/shared'
import leaflet from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Circle, MapContainer, Polygon, TileLayer, useMapEvents } from 'react-leaflet'
import { useEffect } from 'react'

import { hexToRgba, mapCentre } from './zone-draft'

/**
 * The zone map.
 *
 * Leaflet 1.9, which is what legacy draws with — and the reason that matters is
 * not nostalgia: the 31 circles and polygons in the dumps were drawn against
 * *this* projection and *this* hit-test, and `findZoneForPoint` reimplements the
 * same ray-cast. Rendering them with a different engine would mean re-verifying
 * every stored zone against a different notion of "inside".
 *
 * Ported from legacy `pages/delivery-zones.tsx`.
 */

/**
 * Leaflet resolves its marker icons relative to the stylesheet, which a bundler
 * rewrites — so the default marker 404s and nothing renders. Legacy points them
 * at a CDN; kept, because the alternative is bundling three images to draw a pin
 * this screen does not use.
 */
const ICON_BASE = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images'

interface LeafletIconDefault {
  prototype: { _getIconUrl?: unknown }
  mergeOptions: (options: Record<string, string>) => void
}

function patchDefaultIcons() {
  const iconDefault = leaflet.Icon.Default as unknown as LeafletIconDefault
  delete iconDefault.prototype._getIconUrl
  iconDefault.mergeOptions({
    iconUrl: `${ICON_BASE}/marker-icon.png`,
    iconRetinaUrl: `${ICON_BASE}/marker-icon-2x.png`,
    shadowUrl: `${ICON_BASE}/marker-shadow.png`,
  })
}

patchDefaultIcons()

export type DrawMode = 'circle' | 'polygon' | null

/** Turns a map click into either a circle's centre or a polygon's next corner. */
function DrawHandler({
  drawMode,
  onPoint,
}: {
  drawMode: DrawMode
  onPoint: (point: LatLng) => void
}) {
  const map = useMapEvents({
    click(event) {
      if (!drawMode) return
      onPoint({ lat: event.latlng.lat, lng: event.latlng.lng })
    },
  })

  useEffect(() => {
    // The cursor is the only thing telling a manager the map is armed.
    map.getContainer().style.cursor = drawMode ? 'crosshair' : ''
  }, [drawMode, map])

  return null
}

export interface ZoneMapProps {
  zones: DeliveryZone[]
  editingId: string | null
  drawMode: DrawMode
  onPoint: (point: LatLng) => void
}

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTRIBUTION = '© OpenStreetMap'
const DEFAULT_ZOOM = 13

export function ZoneMap({ zones, editingId, drawMode, onPoint }: ZoneMapProps) {
  const centre = mapCentre(zones)

  return (
    <MapContainer
      center={[centre.lat, centre.lng]}
      zoom={DEFAULT_ZOOM}
      className="w-full h-full"
      // Recreated when the centre changes, which is how the map follows a zone
      // that was just drawn without fighting the user's own panning.
      key={`${centre.lat},${centre.lng}`}
    >
      <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
      <DrawHandler drawMode={drawMode} onPoint={onPoint} />

      {zones.map((zone) => {
        // A disabled zone is still drawn, faintly: a manager needs to see where
        // it was before deciding to switch it back on.
        const opacity = zone.enabled ? 1 : 0.35
        const style = {
          color: zone.color,
          weight: zone.id === editingId ? 4 : 2,
          fillColor: zone.color,
          fillOpacity: (zone.id === editingId ? 0.3 : 0.15) * opacity,
          opacity,
        }

        if (zone.type === 'circle') {
          if (!zone.center || !zone.radius) return null
          return (
            <Circle
              key={zone.id}
              center={[zone.center.lat, zone.center.lng]}
              radius={zone.radius}
              pathOptions={{ ...style, fillColor: hexToRgba(zone.color, 1) }}
            />
          )
        }

        if ((zone.polygon?.length ?? 0) < 3) return null
        return (
          <Polygon
            key={zone.id}
            positions={(zone.polygon ?? []).map((point) => [point.lat, point.lng])}
            pathOptions={style}
          />
        )
      })}
    </MapContainer>
  )
}
